import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Store, Palette, Save, ExternalLink, Copy, Eye,
  Tv, Zap, RefreshCw, CheckCircle, XCircle, AlertCircle,
  MessageSquare, Package, Wifi, Plus, Trash2, DollarSign, Loader2
} from 'lucide-react';

export default function StoreSettingsPage() {
  const { api } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    store_name: '',
    store_slug: '',
    logo_url: '',
    banner_url: '',
    primary_color: '#10b981',
    secondary_color: '#059669',
    background_color: '#0f172a',
    text_color: '#ffffff',
    accent_color: '#f59e0b',
    description: '',
    whatsapp_number: '',
    show_trial: true,
    trial_package_id: '',
    connection_id: '',
    store_packages: [],
    footer_text: '',
    trial_message: '',
    purchase_message: '',
    active: false
  });
  const [packages, setPackages] = useState([]);
  const [connections, setConnections] = useState([]);
  const [syncingPackages, setSyncingPackages] = useState(false);
  
  // Slug verification
  const [slugStatus, setSlugStatus] = useState(null);
  const [slugCheckTimeout, setSlugCheckTimeout] = useState(null);
  const [originalSlug, setOriginalSlug] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsRes, connectionsRes] = await Promise.all([
        api.get('/store/settings'),
        api.get('/store/connections')
      ]);
      
      if (settingsRes.data) {
        setSettings({
          store_name: settingsRes.data.store_name || '',
          store_slug: settingsRes.data.store_slug || '',
          logo_url: settingsRes.data.logo_url || '',
          banner_url: settingsRes.data.banner_url || '',
          primary_color: settingsRes.data.primary_color || '#10b981',
          secondary_color: settingsRes.data.secondary_color || '#059669',
          background_color: settingsRes.data.background_color || '#0f172a',
          text_color: settingsRes.data.text_color || '#ffffff',
          accent_color: settingsRes.data.accent_color || '#f59e0b',
          description: settingsRes.data.description || '',
          whatsapp_number: settingsRes.data.whatsapp_number || '',
          show_trial: settingsRes.data.show_trial ?? true,
          trial_package_id: settingsRes.data.trial_package_id || '',
          connection_id: settingsRes.data.connection_id || '',
          store_packages: settingsRes.data.store_packages || [],
          footer_text: settingsRes.data.footer_text || '',
          trial_message: settingsRes.data.trial_message || '',
          purchase_message: settingsRes.data.purchase_message || '',
          active: settingsRes.data.active || false
        });
        setOriginalSlug(settingsRes.data.store_slug || '');
        setPackages(settingsRes.data.available_packages || []);
      }
      
      setConnections(connectionsRes.data || []);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Você precisa ter uma integração Sigma ativa para usar a loja');
      } else {
        toast.error('Erro ao carregar configurações');
      }
    } finally {
      setLoading(false);
    }
  };

  const checkSlugAvailability = useCallback(async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugStatus(null);
      return;
    }
    
    if (slug === originalSlug) {
      setSlugStatus('available');
      return;
    }
    
    setSlugStatus('checking');
    
    try {
      const response = await api.get(`/store/check-slug/${slug}`);
      setSlugStatus(response.data.available ? 'available' : 'taken');
    } catch (error) {
      setSlugStatus('taken');
    }
  }, [api, originalSlug]);

  const handleSlugChange = (value) => {
    const normalizedSlug = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSettings({ ...settings, store_slug: normalizedSlug });
    
    if (slugCheckTimeout) {
      clearTimeout(slugCheckTimeout);
    }
    
    const timeout = setTimeout(() => {
      checkSlugAvailability(normalizedSlug);
    }, 500);
    
    setSlugCheckTimeout(timeout);
  };

  const syncPackages = async () => {
    try {
      setSyncingPackages(true);
      const response = await api.get('/store/sync-packages');
      setPackages(response.data.packages || []);
      toast.success(`${response.data.packages?.length || 0} pacotes sincronizados!`);
    } catch (error) {
      toast.error('Erro ao sincronizar pacotes');
    } finally {
      setSyncingPackages(false);
    }
  };

  // Adicionar pacote à loja
  const addPackageToStore = (pkg) => {
    const exists = settings.store_packages.find(p => p.package_id === pkg.id);
    if (exists) {
      toast.error('Este pacote já está na loja');
      return;
    }
    
    setSettings({
      ...settings,
      store_packages: [...settings.store_packages, {
        package_id: pkg.id,
        package_name: pkg.name,
        price: 0,
        duration_days: 30
      }]
    });
  };

  // Remover pacote da loja
  const removePackageFromStore = (packageId) => {
    setSettings({
      ...settings,
      store_packages: settings.store_packages.filter(p => p.package_id !== packageId)
    });
  };

  // Atualizar preço do pacote
  const updatePackagePrice = (packageId, price) => {
    setSettings({
      ...settings,
      store_packages: settings.store_packages.map(p => 
        p.package_id === packageId ? { ...p, price: parseFloat(price) || 0 } : p
      )
    });
  };

  // Atualizar duração do pacote
  const updatePackageDuration = (packageId, days) => {
    setSettings({
      ...settings,
      store_packages: settings.store_packages.map(p => 
        p.package_id === packageId ? { ...p, duration_days: parseInt(days) || 30 } : p
      )
    });
  };

  const handleSave = async () => {
    if (!settings.store_name.trim()) {
      toast.error('Nome da loja é obrigatório');
      return;
    }
    if (!settings.store_slug.trim()) {
      toast.error('Link da loja é obrigatório');
      return;
    }
    if (slugStatus === 'taken') {
      toast.error('Este link já está em uso. Escolha outro.');
      return;
    }

    try {
      setSaving(true);
      await api.put('/store/settings', settings);
      setOriginalSlug(settings.store_slug);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const getStoreUrl = () => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;
    return `${baseUrl}/loja/${settings.store_slug}`;
  };

  const copyLink = () => {
    navigator.clipboard.writeText(getStoreUrl());
    toast.success('Link copiado!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <Store className="w-8 h-8 text-primary" />
            Minha Loja
          </h1>
          <p className="text-muted-foreground">Configure sua loja pública para vender planos e oferecer testes</p>
        </div>
        <div className="flex items-center gap-2">
          {settings.active && (
            <Button variant="outline" onClick={() => window.open(getStoreUrl(), '_blank')}>
              <Eye className="w-4 h-4 mr-2" />
              Visualizar
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving || slugStatus === 'taken'}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
        </div>
      </div>

      {/* Status da Loja */}
      <Card className="border-2" style={{ borderColor: settings.active ? '#10b981' : '#ef4444' }}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${settings.active ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <Tv className={`w-6 h-6 ${settings.active ? 'text-green-500' : 'text-red-500'}`} />
              </div>
              <div>
                <h3 className="font-bold text-lg">{settings.active ? 'Loja Ativa' : 'Loja Desativada'}</h3>
                <p className="text-sm text-muted-foreground">
                  {settings.active ? 'Sua loja está visível para clientes' : 'Ative para começar a vender'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.active}
              onCheckedChange={(checked) => setSettings({ ...settings, active: checked })}
            />
          </div>
          
          {settings.active && (
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <Label className="text-sm text-muted-foreground">Link da sua loja:</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input value={getStoreUrl()} readOnly className="font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={copyLink}>
                  <Copy className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => window.open(getStoreUrl(), '_blank')}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Informações Básicas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Informações Básicas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Loja</Label>
              <Input
                value={settings.store_name}
                onChange={(e) => setSettings({ ...settings, store_name: e.target.value })}
                placeholder="Ex: TV Premium Brasil"
              />
            </div>

            <div className="space-y-2">
              <Label>Link da Loja (slug)</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">/loja/</span>
                <div className="relative flex-1">
                  <Input
                    value={settings.store_slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="minha-loja"
                    className={`pr-10 ${
                      slugStatus === 'available' ? 'border-green-500' : 
                      slugStatus === 'taken' ? 'border-red-500' : ''
                    }`}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {slugStatus === 'checking' && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                    {slugStatus === 'available' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {slugStatus === 'taken' && <XCircle className="w-4 h-4 text-red-500" />}
                  </div>
                </div>
              </div>
              <p className={`text-xs ${slugStatus === 'taken' ? 'text-red-500' : 'text-muted-foreground'}`}>
                {slugStatus === 'taken' 
                  ? 'Este link já está em uso. Escolha outro.' 
                  : slugStatus === 'available'
                  ? 'Link disponível!'
                  : 'Apenas letras, números e hífens'}
              </p>
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={settings.description}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="Os melhores canais e conteúdos para você!"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>WhatsApp (para contato)</Label>
              <Input
                value={settings.whatsapp_number}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="5511999999999"
              />
            </div>

            <div className="space-y-2">
              <Label>Texto do Rodapé</Label>
              <Input
                value={settings.footer_text}
                onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                placeholder="© 2026 - Todos os direitos reservados"
              />
            </div>
          </CardContent>
        </Card>

        {/* Personalização Visual */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Cores e Aparência
            </CardTitle>
            <CardDescription>
              Para hospedar imagens, use{' '}
              <a href="https://imagem.app/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-medium">
                imagem.app
              </a>
              {' '}e cole o link aqui.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo URL */}
            <div className="space-y-2">
              <Label>Logo da Loja</Label>
              <Input
                value={settings.logo_url}
                onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                placeholder="Cole o link da imagem (ex: https://imagem.app/i/xxx)"
              />
              {settings.logo_url && (
                <div className="mt-2 p-2 bg-muted rounded-lg inline-block">
                  <img src={settings.logo_url} alt="Logo" className="h-12 object-contain" />
                </div>
              )}
            </div>

            {/* Banner URL */}
            <div className="space-y-2">
              <Label>Banner (imagem de fundo)</Label>
              <Input
                value={settings.banner_url}
                onChange={(e) => setSettings({ ...settings, banner_url: e.target.value })}
                placeholder="Cole o link da imagem (ex: https://imagem.app/i/xxx)"
              />
              {settings.banner_url && (
                <div className="mt-2 p-2 bg-muted rounded-lg">
                  <img src={settings.banner_url} alt="Banner" className="h-20 w-full object-cover rounded" />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cor Principal</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cor de Fundo</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.background_color}
                    onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.background_color}
                    onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cor do Texto</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.text_color}
                    onChange={(e) => setSettings({ ...settings, text_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.text_color}
                    onChange={(e) => setSettings({ ...settings, text_color: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Cor de Destaque</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={settings.accent_color}
                    onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer"
                  />
                  <Input
                    value={settings.accent_color}
                    onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conexão WhatsApp */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Conexão WhatsApp
            </CardTitle>
            <CardDescription>
              Selecione a conexão que será usada para enviar mensagens aos clientes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select
              value={settings.connection_id || ''}
              onValueChange={(value) => setSettings({ ...settings, connection_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma conexão" />
              </SelectTrigger>
              <SelectContent>
                {connections.filter(c => c.status === 'connected').map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Wifi className="w-4 h-4 text-green-500" />
                      {conn.name || conn.instance_name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {connections.filter(c => c.status === 'connected').length === 0 && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-yellow-500/10 rounded-lg">
                <AlertCircle className="w-4 h-4 text-yellow-500" />
                Nenhuma conexão ativa. Conecte um WhatsApp primeiro.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configurações de Teste */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Teste Gratuito
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div>
                <h4 className="font-medium">Habilitar Teste</h4>
                <p className="text-sm text-muted-foreground">Visitantes podem solicitar teste</p>
              </div>
              <Switch
                checked={settings.show_trial}
                onCheckedChange={(checked) => setSettings({ ...settings, show_trial: checked })}
              />
            </div>

            {settings.show_trial && (
              <>
                <div className="flex items-center justify-between">
                  <Label>Pacote para Teste</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={syncPackages}
                    disabled={syncingPackages}
                  >
                    {syncingPackages ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sincronizar
                  </Button>
                </div>
                
                <Select
                  value={settings.trial_package_id || ''}
                  onValueChange={(value) => setSettings({ ...settings, trial_package_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um pacote" />
                  </SelectTrigger>
                  <SelectContent>
                    {packages.map((pkg) => (
                      <SelectItem key={pkg.id} value={pkg.id}>
                        {pkg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </CardContent>
        </Card>

        {/* Pacotes para Venda */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Pacotes para Venda
                </CardTitle>
                <CardDescription>
                  Adicione pacotes do Sigma com preços para sua loja
                </CardDescription>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={syncPackages}
                disabled={syncingPackages}
              >
                {syncingPackages ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Sincronizar Pacotes
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Lista de pacotes adicionados */}
            {settings.store_packages.length > 0 && (
              <div className="space-y-3">
                <Label>Pacotes na Loja</Label>
                {settings.store_packages.map((pkg) => (
                  <div key={pkg.package_id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{pkg.package_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          value={pkg.price}
                          onChange={(e) => updatePackagePrice(pkg.package_id, e.target.value)}
                          className="w-24"
                          placeholder="Preço"
                          step="0.01"
                        />
                      </div>
                      <Input
                        type="number"
                        value={pkg.duration_days}
                        onChange={(e) => updatePackageDuration(pkg.package_id, e.target.value)}
                        className="w-20"
                        placeholder="Dias"
                      />
                      <span className="text-sm text-muted-foreground">dias</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removePackageFromStore(pkg.package_id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Adicionar novo pacote */}
            <div className="space-y-2">
              <Label>Adicionar Pacote</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {packages
                  .filter(pkg => !settings.store_packages.find(p => p.package_id === pkg.id))
                  .map((pkg) => (
                    <Button
                      key={pkg.id}
                      variant="outline"
                      size="sm"
                      onClick={() => addPackageToStore(pkg)}
                      className="justify-start max-w-full overflow-hidden"
                      title={pkg.name}
                    >
                      <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
                      <span className="truncate">{pkg.name}</span>
                    </Button>
                  ))}
              </div>
              
              {packages.length === 0 && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-yellow-500/10 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  Clique em "Sincronizar Pacotes" para carregar os pacotes do Sigma.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Mensagens Personalizadas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Mensagens do WhatsApp
            </CardTitle>
            <CardDescription>
              Personalize as mensagens enviadas aos clientes. Use as variáveis disponíveis.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Mensagem de Teste */}
            <div className="space-y-2">
              <Label>Mensagem para Testes Gratuitos</Label>
              <Textarea
                value={settings.trial_message}
                onChange={(e) => setSettings({ ...settings, trial_message: e.target.value })}
                placeholder={`🎉 *ACESSO LIBERADO - {loja}*

👤 *Usuário:* {usuario}
🔐 *Senha:* {senha}
🌐 *DNS:* {dns}

📦 *Pacote:* {pacote}
⏰ *Válido por:* {validade}
📅 *Expira em:* {expiracao}

Obrigado por escolher {loja}! 🙏`}
                className="min-h-[180px] font-mono text-sm"
              />
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                <strong>Variáveis disponíveis:</strong> {'{usuario}'}, {'{senha}'}, {'{dns}'}, {'{pacote}'}, {'{validade}'}, {'{expiracao}'}, {'{loja}'}, {'{nome}'}
              </div>
            </div>

            {/* Mensagem de Compra */}
            <div className="space-y-2">
              <Label>Mensagem para Compras</Label>
              <Textarea
                value={settings.purchase_message}
                onChange={(e) => setSettings({ ...settings, purchase_message: e.target.value })}
                placeholder={`🎉 *PAGAMENTO CONFIRMADO - {loja}*

Olá {nome}! Seu acesso foi liberado.

👤 *Usuário:* {usuario}
🔐 *Senha:* {senha}
🌐 *DNS:* {dns}

📦 *Plano:* {pacote}
📅 *Válido até:* {expiracao}

Obrigado pela preferência! 🙏`}
                className="min-h-[180px] font-mono text-sm"
              />
              <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                <strong>Variáveis disponíveis:</strong> {'{usuario}'}, {'{senha}'}, {'{dns}'}, {'{pacote}'}, {'{expiracao}'}, {'{loja}'}, {'{nome}'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Botão Salvar Mobile */}
      <div className="lg:hidden">
        <Button className="w-full" onClick={handleSave} disabled={saving || slugStatus === 'taken'}>
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salvar Configurações
        </Button>
      </div>
    </div>
  );
}
